import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { AuthContext } from '../contexts/AuthContext';
import ButtonLoader from './ButtonLoader';

import authService from '../services/authService';

import AseLogo from '../assets/ase-logo.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [isLoading, setIsLoading] = useState(false);

  const { setToken } = useContext(AuthContext);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const handleGoogleSignIn = async () => {
      try {
        if (!isLoading) {
          setIsLoading(true);

          const { token } = await authService.googleLogin({ code: searchParams.get('code') });

          setToken(token);
          localStorage.setItem("token", token);
          navigate(searchParams.get('redirect_to') || '/');
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || 'An error occurred. Please try again.');
        setIsLoading(false);
        setSearchParams({});
      }
    }

    if (searchParams.get('code')) {
      if (!isLoading) {
        handleGoogleSignIn();
      }
    }

    if (!isLoading && !searchParams.get('code')) {
      setIsLoading(false);
    }
  }, [searchParams, isLoading, navigate, setSearchParams, setToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsLoading(true);

      const { token } = await authService.login({ email, password });

      setToken(token);
      localStorage.setItem("token", token);
      navigate(searchParams.get('redirect_to') || '/');
    } catch (error) {
      setToken(null);

      localStorage.removeItem("token");

      if (error.response && error.response.data) {
        toast.error(error.response.data?.message);
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  }

  const handleGoogleSignInClicked = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      const { data: { url } } = await authService.getGoogleAuthUrl({
        redirectUri: `${process.env.REACT_APP_APP_URL}/login`
      });

      window.location.href = url;
    } catch (error) {
      console.log(error);
      toast.error('An error occurred. Please try again.');
    }
  };

  return (
    <div className="flex flex-col items-center justify-center flex-grow min-h-screen px-2">
      <Helmet>
        <title>Login | ASE Group</title>
      </Helmet>

      <img src={AseLogo} alt="ASE Group" className='h-[50px] mb-6' />

      <form
        className='border border-gray-300 rounded-lg flex flex-col gap-4 w-full md:w-[550px] p-[32px] mb-4 shadow-md bg-white'
        onSubmit={handleSubmit}
      >
        <label className='flex flex-col gap-1'>
          <span>Email</span>

          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className='flex flex-col gap-1'>
          <span>Password</span>

          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        <div className="flex items-center justify-between mb-2">
          <label className="flex items-center gap-1">
            <input type="checkbox" />
            <span className="text-black font-light text-sm">Remember me</span>
          </label>

          <Link to="/forgot-password" className="text-ase-blue font-semibold text-sm">
            Forgot password?
          </Link>
        </div>

        <button type="submit" className="w-full bg-ase-blue font-semibold text-white h-[48px] rounded-md flex items-center justify-center">
          {isLoading ? <ButtonLoader /> : 'Sign In'}
        </button>

        <button
          onClick={handleGoogleSignInClicked}
          className="w-full border border-ase-blue text-ase-blue font-semibold h-[48px] rounded-md"
        >
          Sign In with Google
        </button>

        <p className='text-xs text-center'>
          By using this app, you're agreeing to our
          <Link className='text-ase-blue' to="/privacy-policy" target='_blank'> Privacy Policy.</Link>
        </p>
      </form>
    </div>
  );
};

export default Login;
